import type { Screen0LanguageCode } from "@/features/language/language-registry";
import {
  ruleWindowOverlapsBlockWindow,
  targetAppKey,
  windowFromHours,
} from "../rules/ruleDerivation";
import type { Rule, RuleWindow, Target } from "../rules/ruleTypes";
import {
  buildSmartBlockSuggestions,
  type BlockWindow,
  type SmartBlockCandidate,
  type SmartBlockCopy,
} from "./smartBlockEngine";
import type { SmartBlockSuggestion } from "./insightScoring";

export type SmartBlockSuggestionMode = "activate" | "reactivate";

export type ResolvedSmartBlockSuggestion = SmartBlockSuggestion & {
  mode: SmartBlockSuggestionMode;
  existingRuleId?: string;
  reactivateHint?: string;
};

const MAX_VISIBLE_SUGGESTIONS = 5;
/** Build enough ranked suggestions so backfill can skip enabled-rule apps (up to 2 per app). */
const SUGGESTION_POOL_SIZE = 24;

function windowsMatch(a: RuleWindow, b: RuleWindow): boolean {
  return a.startMinute === b.startMinute && a.durationMinutes === b.durationMinutes;
}

function suggestionWindow(suggestion: SmartBlockSuggestion): RuleWindow {
  return windowFromHours(suggestion.startHour, suggestion.endHour);
}

function ruleContainsApp(rule: Rule, appKey: string, targetsById: Map<string, Target>): boolean {
  for (const targetId of rule.targetIds) {
    const target = targetsById.get(targetId);
    if (target?.apps.some((app) => app.key === appKey)) return true;
  }
  return false;
}

function isEnabledSmartBlockRuleForApp(
  rule: Rule,
  appKey: string,
  targetsById: Map<string, Target>,
): boolean {
  return rule.enabled && rule.source === "smart_block" && ruleContainsApp(rule, appKey, targetsById);
}

function enabledSmartBlockWindowsByApp(
  rules: Rule[],
  targetsById: Map<string, Target>,
): Map<string, RuleWindow[]> {
  const byApp = new Map<string, RuleWindow[]>();
  for (const rule of rules) {
    if (!rule.enabled || rule.source !== "smart_block") continue;
    for (const targetId of rule.targetIds) {
      const target = targetsById.get(targetId);
      if (!target) continue;
      for (const app of target.apps) {
        for (const mapKey of [
          app.key,
          targetAppKey({ displayName: app.label, appIdentifier: app.appIdentifier }),
        ]) {
          const existing = byApp.get(mapKey) ?? [];
          existing.push(rule.window);
          byApp.set(mapKey, existing);
        }
      }
    }
  }
  return byApp;
}

/**
 * Legacy Smart Block rules without `smartBlockGoal` only suppress suggestions on
 * exact window match — not the whole metric. Rules with a stored goal suppress
 * any suggestion for that metric on the same app. All enabled Smart Block rules
 * for the app also suppress overlapping windows regardless of metric.
 */
export function suggestionHiddenByEnabledRules(
  suggestion: SmartBlockSuggestion,
  rules: Rule[],
  targetsById: Map<string, Target>,
): boolean {
  const appKey = targetAppKey({
    displayName: suggestion.displayName,
    appIdentifier: suggestion.appIdentifier,
  });
  const window = suggestionWindow(suggestion);
  const blockWindow: BlockWindow = { startHour: suggestion.startHour, endHour: suggestion.endHour };

  for (const rule of rules) {
    if (!isEnabledSmartBlockRuleForApp(rule, appKey, targetsById)) continue;

    if (rule.smartBlockGoal === suggestion.goal) return true;

    // Legacy: no persisted goal — treat as covering only the exact window.
    if (!rule.smartBlockGoal && windowsMatch(rule.window, window)) return true;

    if (ruleWindowOverlapsBlockWindow(rule.window, blockWindow)) return true;
  }
  return false;
}

/** Finds a rule whose target includes the app, window matches, and goal aligns when stored. */
export function findMatchingRule(
  suggestion: SmartBlockSuggestion,
  rules: Rule[],
  targetsById: Map<string, Target>,
): Rule | null {
  const appKey = targetAppKey({
    displayName: suggestion.displayName,
    appIdentifier: suggestion.appIdentifier,
  });
  const window = suggestionWindow(suggestion);

  for (const rule of rules) {
    if (!windowsMatch(rule.window, window)) continue;
    if (!ruleContainsApp(rule, appKey, targetsById)) continue;
    if (rule.smartBlockGoal && rule.smartBlockGoal !== suggestion.goal) continue;
    return rule;
  }
  return null;
}

/**
 * Filters Smart Block suggestions against live rules: hides enabled matches,
 * marks disabled matches for reactivation, and backfills up to five cards.
 */
export function resolveSmartBlockSuggestionsAgainstRules(
  candidates: SmartBlockCandidate[],
  fallbackWindow: BlockWindow | null,
  rules: Rule[],
  targets: Target[],
  copy: SmartBlockCopy,
  languageCode: Screen0LanguageCode = "en",
): ResolvedSmartBlockSuggestion[] {
  const targetsById = new Map(targets.map((target) => [target._id, target]));
  const blockedByApp = enabledSmartBlockWindowsByApp(rules, targetsById);
  const pool = buildSmartBlockSuggestions(
    candidates,
    fallbackWindow,
    copy,
    SUGGESTION_POOL_SIZE,
    blockedByApp,
    languageCode,
  );

  const resolved: ResolvedSmartBlockSuggestion[] = [];
  for (const suggestion of pool) {
    if (resolved.length >= MAX_VISIBLE_SUGGESTIONS) break;

    if (suggestionHiddenByEnabledRules(suggestion, rules, targetsById)) continue;

    const match = findMatchingRule(suggestion, rules, targetsById);
    if (match?.enabled) continue;

    if (match && !match.enabled) {
      resolved.push({
        ...suggestion,
        mode: "reactivate",
        existingRuleId: match._id,
        reactivateHint: copy.reactivateHint,
      });
    } else {
      resolved.push({ ...suggestion, mode: "activate" });
    }
  }
  return resolved;
}
